import { useTheme } from "@mui/material";
import { useEffect, useState } from "react";

const GLYPHS = ["·", "✢", "✴", "✳", "✻", "✽"];
const GLYPH_INTERVAL_MS = 180;
const DEFAULT_LABEL_STEP_MS = 3_000;

interface InProgressIndicatorProps {
  label?: string;
  // When provided, labels advance every `labelStepMs` and stay on the last one.
  // Use to keep the user oriented during longer waits (e.g. ["Updating…",
  // "Almost there…", "Hang tight…"]).
  labels?: string[];
  labelStepMs?: number;
  compact?: boolean;
}

export default function InProgressIndicator({
  label = "Updating…",
  labels,
  labelStepMs = DEFAULT_LABEL_STEP_MS,
  compact = false,
}: InProgressIndicatorProps): JSX.Element {
  const theme = useTheme();
  const [frame, setFrame] = useState(0);
  const [labelIdx, setLabelIdx] = useState(0);

  useEffect(() => {
    const interval = setInterval(() => {
      setFrame((f) => (f + 1) % GLYPHS.length);
    }, GLYPH_INTERVAL_MS);
    return () => clearInterval(interval);
  }, []);

  useEffect(() => {
    if (!labels || labels.length <= 1) return undefined;
    setLabelIdx(0);
    const interval = setInterval(() => {
      setLabelIdx((idx) => (idx < labels.length - 1 ? idx + 1 : idx));
    }, labelStepMs);
    return () => clearInterval(interval);
  }, [labels, labelStepMs]);

  const displayLabel = labels?.[labelIdx] ?? label;

  return (
    <div
      style={{
        padding: compact ? 0 : "8px 14px",
        display: "flex",
        alignItems: "center",
        gap: 8,
        color: theme.palette.text.secondary,
        fontSize: "0.875rem",
      }}
    >
      <span
        aria-hidden
        style={{
          display: "inline-block",
          width: "1em",
          textAlign: "center",
          color: theme.palette.primary.main,
          fontSize: "1rem",
          lineHeight: 1,
        }}
      >
        {GLYPHS[frame]}
      </span>
      <span>{displayLabel}</span>
    </div>
  );
}
